import { useCallback, useEffect, useRef, useState } from "react";
import { TakeoverCard } from "@/components/TakeoverCard";
import { PhotoActionDialog } from "@/components/PhotoActionDialog";
import { DeleteOrderButton } from "@/components/DeleteOrderButton";
import { useAuth } from "@/context/AuthContext";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Paperclip, Upload, History, CalendarClock, AlertTriangle, FileText, CheckSquare, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { StatusBadge, PriorityBadge, OverdueBadge, DemoBadge, NativeSelect } from "@/components/common";
import { ActionDialog, ACTIONS } from "@/components/ActionDialog";
import { fmtDT, fmtD, IMPEDIMENTS } from "@/lib/format";
import { cn } from "@/lib/utils";

const Info = ({ label, children, testid }) => (
  <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p><p className="mt-0.5 font-semibold" data-testid={testid}>{children || "—"}</p></div>
);

const Card = ({ title, icon: Icon, children, testid }) => (
  <section className="card-soft p-5" data-testid={testid}>
    <h3 className="mb-3 flex items-center gap-2 font-bold">{Icon && <Icon className="h-4 w-4 text-primary" />}{title}</h3>
    {children}
  </section>
);

const Attachments = ({ o, onChange }) => {
  const [kind, setKind] = useState("prima");
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    try {
      const { data } = await api.post(`/orders/${o.id}/attachments`, fd);
      onChange(data);
      toast.success("Allegato caricato");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      ref.current.value = "";
    }
  };
  const view = async (a) => {
    const r = await api.get(`/files/${a.id}`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  return (
    <Card title="Fotografie e allegati" icon={Paperclip} testid="order-attachments">
      {o.can.upload && (
        <div className="mb-3 flex flex-wrap gap-2">
          <NativeSelect testid="attachment-kind-select" value={kind} onChange={setKind}>
            <option value="documento">Documento</option><option value="altro">Altro</option>
          </NativeSelect>
          <input ref={ref} type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="attachment-file-input" />
          <Button variant="outline" disabled={busy} onClick={() => ref.current.click()} className="h-10 rounded-xl" data-testid="attachment-upload-button"><Upload className="mr-1 h-4 w-4" />{busy ? "Caricamento…" : "Scatta / carica"}</Button>
        </div>
      )}
      {o.attachments.length === 0 && <p className="text-sm text-muted-foreground">Nessun allegato.</p>}
      <ul className="space-y-2">
        {o.attachments.map((a) => (
          <li key={a.id}><button onClick={() => view(a)} className="flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-left text-sm hover:bg-muted" data-testid={`attachment-${a.id}`}>
            <FileText className="h-4 w-4 text-primary" /><span className="rounded bg-accent px-1.5 text-xs font-bold uppercase">{a.kind}</span><span className="flex-1 truncate">{a.filename}</span><span className="text-xs text-muted-foreground">{a.by_name} · {fmtDT(a.at)}</span>
          </button></li>
        ))}
      </ul>
    </Card>
  );
};

const Checklist = ({ o, onChange }) => {
  const toggle = async (c) => {
    try {
      const { data } = await api.post(`/orders/${o.id}/checklist/${c.id}`, { done: !c.done });
      onChange(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!o.checklist.length) return null;
  return (
    <Card title={`Checklist (${o.checklist.filter((c) => c.done).length}/${o.checklist.length})`} icon={CheckSquare} testid="order-checklist">
      <ul className="space-y-2">
        {o.checklist.map((c) => (
          <li key={c.id}><button disabled={!o.can.checklist} onClick={() => toggle(c)} data-testid={`checklist-item-${c.id}`}
            className={cn("flex min-h-[52px] w-full items-center gap-3 rounded-xl border px-4 text-left", c.done && "bg-emerald-50 border-emerald-200", !o.can.checklist && "opacity-70")}>
            {c.done ? <CheckSquare className="h-6 w-6 text-emerald-600" /> : <Square className="h-6 w-6 text-zinc-400" />}
            <span className="flex-1 font-semibold">{c.label}</span>
            {c.done && <span className="text-xs text-muted-foreground">{c.done_by_name} · {fmtDT(c.done_at)}</span>}
          </button></li>
        ))}
      </ul>
    </Card>
  );
};

const PRIMARY = ["take", "start", "resume", "complete", "approve"];
const EVENT_LABEL = { priorita_modificata: "Priorità modificata", affidato: "Affidato ai responsabili", affidamento_chiuso: "Affidamento chiuso", scadenza_modificata: "Scadenza modificata" };

export default function OrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const [action, setAction] = useState(null);
  const { user } = useAuth();
  const load = useCallback(() => {
    api.get(`/orders/${id}`).then((r) => setO(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  const direct = async (a) => {
    try {
      const { data } = await api.post(`/orders/${id}/action`, { action: a });
      setO(data);
      toast.success(`${ACTIONS[a].label}: registrato`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (err) return <div className="card-soft p-8 text-center" data-testid="order-error"><p className="font-bold text-red-600">{err}</p><Link to="/ordini" className="mt-3 inline-block text-primary">Torna agli ordini</Link></div>;
  if (!o) return <p className="text-muted-foreground">Caricamento…</p>;
  const available = Object.keys(ACTIONS).filter((a) => o.can[a]);
  const primary = available.filter((a) => PRIMARY.includes(a));
  const secondary = available.filter((a) => !PRIMARY.includes(a));

  return (
    <div data-testid="order-detail-page" className="fade-up">
      <Link to="/ordini" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground"><ArrowLeft className="h-4 w-4" />Ordini</Link>
      <div className={cn("card-soft mb-5 p-5 sm:p-6", o.is_overdue && "ring-2 ring-red-500")}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm text-muted-foreground" data-testid="order-code">{o.code}</span>
          <StatusBadge status={o.status} testid="order-status-badge" /><PriorityBadge priority={o.priority} />
          <OverdueBadge order={o} testid="order-overdue-badge" /><DemoBadge show={o.is_demo} />
          {o.was_late && !o.is_overdue && <span className="rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700" data-testid="order-was-late">Ritardo registrato</span>}
        </div>
        <h1 className="mt-2 text-2xl font-extrabold sm:text-3xl" data-testid="order-title">{o.title}</h1>
        {o.description && <p className="mt-2 text-sm text-muted-foreground">{o.description}</p>}
        <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
          {o.work_types?.length > 0 && <Info label="Tipo di lavoro" testid="order-work-types">{o.work_types.join(", ")}</Info>}
          <Info label="Commessa">{o.commessa_code}</Info><Info label="Cimitero">{o.cimitero_name}</Info>
          <Info label="Area">{o.area}</Info><Info label="Responsabile">{o.responsabile_name}</Info>
          <Info label="Assegnatari" testid="order-assignees">{o.assignee_names.join(", ")}</Info>
          {o.delegated_names?.length > 0 && <Info label={o.delegation_open ? "Affidato a (da assegnare)" : "Affidato a"} testid="order-delegated">{o.delegated_names.join(", ")}</Info>}
          {o.work_date && <Info label="Data" testid="order-work-date">{fmtD(`${o.work_date}T12:00:00`)}</Info>}
          <Info label="Scadenza attuale" testid="order-due-current"><span className={o.is_overdue ? "text-red-600" : ""}>{fmtDT(o.due_current)}</span></Info>
          <Info label="Scadenza originale" testid="order-due-original">{fmtDT(o.due_original)}</Info>
          <Info label="Creato da">{o.created_by_name} · {fmtDT(o.created_at)}</Info>
          {o.recurrence_id && <Info label="Ordine ricorrente" testid="order-recurrence"><Link to="/ricorrenze" className="text-primary">Orario {fmtDT(o.planned_start)} → {new Date(o.due_original).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</Link></Info>}
        </div>
        {o.criticita_id && <Link to={`/criticita/${o.criticita_id}`} className="mt-4 inline-block text-sm font-semibold text-primary" data-testid="order-criticita-link">Criticità collegata →</Link>}
        {o.required_qualifications?.length > 0 && <p className="mt-4 text-xs"><b>Abilitazioni richieste:</b> {o.required_qualifications.join(", ")}</p>}
      </div>

      {o.docs_request && (
        <div className="mb-5 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm" data-testid="order-docs-request">
          <b>Documentazione richiesta:</b> {o.docs_request.note} {o.docs_request.fulfilled_at && <span className="ml-2 font-semibold text-emerald-700">· allegato caricato {fmtDT(o.docs_request.fulfilled_at)}</span>}
        </div>
      )}
      {o.is_overdue && o.delay_reasons.length === 0 && (
        <div className="mb-5 flex items-start gap-3 rounded-2xl bg-red-600 p-4 text-white" data-testid="order-missing-motivation">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" /><p className="text-sm font-semibold">Ordine scaduto senza motivazione. L'assegnatario o il responsabile deve indicare la causa del ritardo.</p>
        </div>
      )}

      {available.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="order-actions">
          <div className="grid gap-2 sm:flex sm:flex-wrap">
            {primary.map((a) => (
              <Button key={a} onClick={() => (ACTIONS[a].direct && a !== "start" ? direct(a) : setAction(a))} className="min-h-[56px] rounded-xl px-6 text-base font-bold sm:min-h-[48px]" data-testid={`order-action-${a}`}>{ACTIONS[a].label}</Button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {secondary.map((a) => (
              <Button key={a} variant={ACTIONS[a].tone === "destructive" ? "destructive" : "outline"} onClick={() => setAction(a)} className="h-11 rounded-xl font-semibold" data-testid={`order-action-${a}`}>{ACTIONS[a].label}</Button>
            ))}
            {["ceo", "amministrazione"].includes(user.role) && <DeleteOrderButton order={o} />}
          </div>
        </div>
      )}
      {available.length === 0 && ["ceo", "amministrazione"].includes(user.role) && <div className="mb-6"><DeleteOrderButton order={o} /></div>}

      <div className="grid gap-5 lg:grid-cols-[1fr_420px]">
        <div className="space-y-5">
          <Checklist o={o} onChange={setO} />
          <TakeoverCard type="work_order" targetId={o.id} closed={["chiuso", "annullato"].includes(o.status)} />
          <Attachments o={o} onChange={setO} />
          {(o.delay_reasons.length > 0 || o.due_history.length > 0) && (
            <Card title="Ritardi e scadenze" icon={CalendarClock} testid="order-delays">
              {o.delay_reasons.map((r) => (
                <div key={r.id} className="mb-2 rounded-xl border-l-4 border-red-500 bg-red-50/60 px-3 py-2 text-sm" data-testid="order-delay-reason">
                  <b>{IMPEDIMENTS[r.category] || r.category}</b>{r.kind === "sospensione" && " (sospensione)"} — {r.text}<p className="text-xs text-muted-foreground">{r.by_name} · {fmtDT(r.at)}</p>
                </div>
              ))}
              {o.due_history.map((h, i) => (
                <div key={i} className="mb-2 rounded-xl border px-3 py-2 text-sm" data-testid="order-due-history-item">
                  Scadenza da <b>{fmtDT(h.old)}</b> a <b>{fmtDT(h.new)}</b> — {h.reason}<p className="text-xs text-muted-foreground">{h.by_name} · {fmtDT(h.at)}</p>
                </div>
              ))}
            </Card>
          )}
        </div>
        <Card title="Storico" icon={History} testid="order-timeline">
          <ol className="relative space-y-4 border-l-2 border-accent pl-5">
            {o.events.slice().reverse().map((e) => (
              <li key={e.id} className="relative" data-testid={`order-event-${e.type}`}>
                <span className="absolute -left-[27px] top-1 h-3 w-3 rounded-full border-2 border-white bg-primary" />
                <p className="text-sm font-semibold first-letter:uppercase">{EVENT_LABEL[e.type] || e.type.replaceAll("_", " ")}{e.to_status && e.from_status && e.to_status !== e.from_status ? ` → ${e.to_status.replaceAll("_", " ")}` : ""}</p>
                {e.note && <p className="text-sm text-muted-foreground">{e.note}</p>}
                <p className="text-xs text-muted-foreground">{e.actor_name} · {fmtDT(e.at)}</p>
              </li>
            ))}
          </ol>
        </Card>
      </div>
      {action && (["start", "complete"].includes(action) ? <PhotoActionDialog order={o} action={action} onClose={() => setAction(null)} onDone={setO} />
        : <ActionDialog order={o} action={action} onClose={() => setAction(null)} onDone={setO} />)}
    </div>
  );
}
